import { CircularProgress, Grid, LinearProgress } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { Formik } from 'formik';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect, useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import { ErrorAlert } from '../../components/atoms/Alert-message';
import Button from '../../components/atoms/button';
import Toast, {
  showToasterFailure,
  showToasterSuccess,
} from '../../components/atoms/toast';
import DriverSection from '../../components/OverviewPage/DriverSection';
import OverviewInput from '../../components/OverviewPage/OverviewInput';
import OverviewUploadFiles from '../../components/OverviewPage/OverviewUploadFiles';
import AlreadySubmitted from '../../components/shared/AlreadySubmitted';
import Title from '../../components/shared/Title';
import useOverviewData from '../../hooks/useOverviewData';
import { APIHeader, APIHeaderForUpload } from '../../index';
import {
  createGetFilesOptions,
  createGetLSCOptions,
  createGetSiteNameOptions,
} from '../../queries/overviewQueries';
import { errorNotification } from '../../reducers/handle-api-status/slice';
import { clearOverviewData, overview } from '../../reducers/overview/slice';
import { pecrData } from '../../reducers/pecr-info/slice';
import {
  homeButtonComponent,
  listDiv,
  overviewContainers,
  pageGrid,
  progressBar,
} from '../../styles';
import { getDownloadLink, getURL, validateInputFields } from '../../utils';

// P/ECR Over-View Page Component
function CreateOverviewPage(props) {
  const { overviewInfo, insertId, apiConfig, userInfo, errorInAPI } = props;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [siteName, setSiteName] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [displayFiles, setDisplayFiles] = useState([]);
  const [LSCMailAddress, setLSCMailAddress] = useState('');
  const [showProgress, setShowProgress] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [inputValidationMessage, setInputValidationMessage] = useState({
    Title: '',
    Description: '',
    WindchillECRUrl: '',
  });
  const [disableButton, setDisableButton] = useState(true);
  const [isDisabled, setIsDisabled] = useState(true);
  const defValus = props.overviewInfo || { Title: '', Description: '' };
  const [defaultValues, setDefaultValues] = useState({
    ...defValus,
    StatusID: null,
  });

  const { id } = useParams();
  let pecrId;
  console.log('id', id);

  if (id) {
    pecrId = id;
  } else {
    pecrId = insertId;
  }

  const { overviewData, isOverviewLoading, overviewError, isUserAuthorized } =
    useOverviewData(pecrId, userInfo, dispatch, t);

  const {
    data: fileData,
    isLoading: isFileLoading,
    error: fileError,
  } = useQuery(
    createGetFilesOptions(
      { mainFolder: 'PECROverview', subFolder: pecrId },
      { enabled: !!pecrId }
    )
  );

  const {
    data: siteNameData,
    isLoading: isSiteNameLoading,
    error: siteNameError,
  } = useQuery(
    createGetSiteNameOptions(
      { userEmail: userInfo?.userEmail },
      { enabled: !!userInfo?.userEmail }
    )
  );

  const {
    data: lscData,
    isLoading: isLSCLoading,
    error: lscError,
  } = useQuery(
    createGetLSCOptions(
      {
        pecrId: pecrId || 0,
        ID: siteNameData?.ID,
      },
      { enabled: !!siteNameData?.ID }
    )
  );

  useEffect(() => {
    if (overviewError || fileError || siteNameError || lscError) {
      dispatch(
        errorNotification({
          hasErrorOccured: true,
          message: t('error_message'),
        })
      );
    }
  }, [overviewError, fileError, siteNameError, lscError, dispatch, t]);

  // Set site name and LSC email from query results
  useEffect(() => {
    if (siteNameData) {
      setSiteName(siteNameData);
    }
  }, [siteNameData]);

  useEffect(() => {
    if (lscData && lscData[0]?.Email) {
      setLSCMailAddress(lscData[0].Email);
    }
  }, [lscData]);

  /***** Sucess call back for Navigation after the save button is tiggered ****/
  const successCallBack = id => {
    navigate(`/TrackingFile?id=${id || pecrId}`);
    dispatch(clearOverviewData({}));
    // navigate(`../PecrSummary`);
  };

  const url = getURL();

  useEffect(() => {
    if (!pecrId) {
      return;
    }
    if (pecrId) {
      setDefaultValues({ ...overviewData });
      setDisplayFiles(fileData);
    } else {
      dispatch(overview({}));
      setDefaultValues({ StatusID: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overviewData, fileData]);

  const getValidationSchema = () => {
    return Yup.object().shape({
      Title: Yup.string().required('Required'),
      Description: Yup.string().required('Required'),
    });
  };

  const getTogglevalue = value => {
    let toggleState = 'No';
    if (value === 1) {
      toggleState = 'Partial';
    } else if (value === 2) {
      toggleState = 'Yes';
    }
    return toggleState;
  };

  // Handle Submit button for the Posting the Data in DB
  const handleSubmit = values => {
    setShowProgress(true);
    const isPut = pecrId || props.insertId;
    fetch(`${apiConfig.uiURL}api/PECRCreateUpdate`, {
      headers: APIHeader(),
      method: isPut ? 'PUT' : 'POST',
      body: JSON.stringify({
        ...values,
        StatusID: 2,
        pecrId: pecrId || props.insertId,
        UserID: userInfo.userID,
      }),
    })
      .then(async res => {
        const resp = await res.json();
        !isPut && dispatch(pecrData({ key: 'insertId', value: resp.insertId }));

        successCallBack(resp.insertId);

        const emailPromises = [
          fetch(`${apiConfig.uiURL}api/sendEmail`, {
            headers: APIHeader(),
            method: 'POST',
            body: JSON.stringify({
              PecrId: pecrId || resp.insertId,
              toAddress: userInfo.userEmail,
              Title: values.Title,
              Description: values.Description,
              fromAddress: 'PECR@carrier.com',
              type: 'assignEmail',
              URL: `${url}LocalSiteReview/${pecrId || resp.insertId}`,
              Factory: siteName.SiteName,
              Strategic: getTogglevalue(values.Strategic),
              EHSI: getTogglevalue(values.Safety),
              RI: getTogglevalue(values.Regulatory),
              ECI: getTogglevalue(values.CustomerImpact),
              TA: getTogglevalue(values.Turnback),
              WCI: getTogglevalue(values.Warranty),
              PPS: getTogglevalue(values.StopProduction),
              STD: getTogglevalue(values.Standardization),
              CR: getTogglevalue(values.CostReduction),
              toWhome: 'Local Creator',
              Comments: '',
              creator: userInfo.name,
              creatorsite: siteName.SiteName,
            }),
          }).catch(() => {
            dispatch(
              errorNotification({
                hasErrorOccured: true,
                message: t('error_message'),
              })
            );
          }),

          fetch(`${apiConfig.uiURL}api/sendEmail`, {
            headers: APIHeader(),
            method: 'POST',
            body: JSON.stringify({
              PecrId: pecrId || resp.insertId,
              toAddress: LSCMailAddress,
              loginUserEmail: userInfo.userEmail,
              Title: values.Title,
              Description: values.Description,
              fromAddress: 'PECR@carrier.com',
              type: 'assignEmail',
              URL: `${url}LocalSiteReview/${pecrId || resp.insertId}`,
              Factory: siteName.SiteName,
              Strategic: getTogglevalue(values.Strategic),
              EHSI: getTogglevalue(values.Safety),
              RI: getTogglevalue(values.Regulatory),
              ECI: getTogglevalue(values.CustomerImpact),
              TA: getTogglevalue(values.Turnback),
              WCI: getTogglevalue(values.Warranty),
              PPS: getTogglevalue(values.StopProduction),
              STD: getTogglevalue(values.Standardization),
              CR: getTogglevalue(values.CostReduction),
              toWhome: 'Overview Submit',
              Comments: '',
              creator: userInfo.name,
              creatorsite: siteName.SiteName,
            }),
          }).catch(() => {
            dispatch(
              errorNotification({
                hasErrorOccured: true,
                message: t('error_message'),
              })
            );
          }),
        ];

        const uploadData = new FormData();
        for (const file of uploadedFiles) {
          uploadData.append('file', file);
        }
        uploadData.append('MainFolder', 'PECROverview');
        uploadData.append('SubFolder', pecrId || resp.insertId);

        const fileUploadPromise = fetch(`${apiConfig.uiURL}api/uploadDoc`, {
          headers: APIHeaderForUpload(),
          method: 'POST',
          body: uploadData,
        })
          .then(async res => {
            if (res.status === 200) {
              //stop showing progress bar
              setShowProgress(false);
            }
          })
          .catch(() => {
            setShowProgress(false);
            dispatch(
              errorNotification({
                hasErrorOccured: true,
                message: t('error_message'),
              })
            );
          });

        Promise.all([...emailPromises, fileUploadPromise]).catch(error => {
          console.error('Background tasks error:', error);
        });
      })
      .catch(error => {
        console.error('Error in handleSubmit:', error);
        dispatch(
          errorNotification({
            hasErrorOccured: true,
            message: t('error_message'),
          })
        );
      });
  };

  const onCancelClick = () => {
    dispatch(clearOverviewData({}));
    navigate('../');
  };

  const handleOnTextChange = (event, key, setFieldValue) => {
    setFieldValue(key, event.target.value);
    dispatch(overview({ [key]: event.target.value }));
    const inputValidation = validateInputFields(event.target.value, t);
    setInputValidationMessage({
      ...inputValidationMessage,
      [key]: inputValidation,
    });
  };

  const handleOnChange = (key, option, isMulti = false, setFieldValue) => {
    setFieldValue(
      key,
      isMulti ? option.map(each => each.value).join() : option.value
    );
    setFieldValue(
      key + 'label',
      isMulti ? option.map(each => each.label).join() : option.label
    );
    dispatch(
      overview({
        [key]: isMulti ? option.map(each => each.value).join() : option.value,
      })
    );
  };

  const handleOnToggle = (key, value, setFieldValue) => {
    setFieldValue(key, value);
    dispatch(overview({ [key]: value }));
  };

  const removeDoc = (event, fileName) => {
    event.preventDefault();
    const remainingFile = displayFiles.filter(file => {
      return file !== fileName;
    });
    setDisplayFiles(remainingFile);
    const remainingUploadFile = uploadedFiles.filter(file => {
      return file.name !== fileName;
    });
    setUploadedFiles(remainingUploadFile);
    if (pecrId) {
      fetch(`${apiConfig.uiURL}api/DeleteFile`, {
        method: 'POST',
        headers: APIHeader(),
        body: JSON.stringify({
          MainFolder: 'PECROverview',
          SubFolder: pecrId,
          fileName: fileName,
          PID: pecrId,
          folderName: 'OverView',
          email: userInfo.userEmail,
        }),
      }).catch(() => {
        dispatch(
          errorNotification({
            hasErrorOccured: true,
            message: t('error_message'),
          })
        );
      });
    }
    setErrorMessage('');
  };

  const onDownloadClick = filename => {
    fetch(`${apiConfig.uiURL}api/DownloadFilesForLSC`, {
      method: 'POST',
      headers: APIHeader(),
      body: JSON.stringify({
        MainFolder: 'PECROverview',
        SubFolder: pecrId,
        filename: filename,
      }),
    })
      .then(async response => {
        response.blob().then(blob => {
          getDownloadLink(blob, filename);
        });
      })
      .catch(() => {
        dispatch(
          errorNotification({
            hasErrorOccured: true,
            message: t('error_message'),
          })
        );
      });
  };

  const onSaveClick = values => {
    const isPut = pecrId || props.insertId;
    fetch(`${apiConfig.uiURL}api/PECRCreateUpdate`, {
      headers: APIHeader(),
      method: isPut ? 'PUT' : 'POST',
      body: JSON.stringify({
        ...values,
        StatusID: 1,
        pecrId: pecrId || props.insertId,
        UserID: userInfo.userID,
      }),
    })
      .then(async res => {
        showToasterSuccess(t('Save_message'));
        const response = await res.json();
        const uploadData = new FormData();
        for (const file of uploadedFiles) {
          uploadData.append('file', file);
        }
        uploadData.append('MainFolder', 'PECROverview');
        uploadData.append('SubFolder', pecrId || response.insertId);
        fetch(`${apiConfig.uiURL}api/uploadDoc`, {
          headers: APIHeaderForUpload(),
          method: 'POST',
          body: uploadData,
        })
          .then(async res => {
            if (res.status === 200) {
              navigate('../List');
            }
          })
          .catch(() => {
            dispatch(
              errorNotification({
                hasErrorOccured: true,
                message: t('error_message'),
              })
            );
          });
      })
      .catch(() => {
        showToasterFailure(t('Save_failed_message'));
        dispatch(
          errorNotification({
            hasErrorOccured: true,
            message: t('error_message'),
          })
        );
      });
  };

  // Early returns
  if (errorInAPI.hasErrorOccured) {
    return <ErrorAlert message={errorInAPI.message} />;
  }

  if (
    !userInfo ||
    userInfo.userId === '' ||
    isOverviewLoading ||
    isFileLoading ||
    isSiteNameLoading ||
    isLSCLoading
  ) {
    return (
      <Grid style={homeButtonComponent}>
        <CircularProgress />
      </Grid>
    );
  }

  if (
    userInfo?.active?.toString() !== '1' ||
    userInfo?.userId?.toString() === '0' ||
    userInfo?.userId === null ||
    !isUserAuthorized
  ) {
    return (
      <Grid style={homeButtonComponent}>{t('No_active_user_message')}</Grid>
    );
  }

  return (
    <>
      <Formik
        enableReinitialize={true}
        initialValues={defaultValues}
        validationSchema={getValidationSchema()}
        onSubmit={handleSubmit}
        validateOnChange={() => {}}
        validateOnBlur={() => {}}
      >
        {({ handleSubmit, setFieldValue, values = {}, isValid, dirty }) => {
          console.log('values', values);
          const { ProductFamilyID = '' } = values;

          const hasValidationErrors =
            inputValidationMessage.Title !== '' ||
            inputValidationMessage.Description !== '' ||
            inputValidationMessage.WindchillECRUrl !== '';

          const hasRequiredFields =
            values.Title && values.Description && values.ProductFamilyID;

          const shouldDisableButton = hasValidationErrors || !hasRequiredFields;
          const shouldDisableForm =
            values.StatusID !== null && Number(values.StatusID) !== 1;

          if (disableButton !== shouldDisableButton) {
            setDisableButton(shouldDisableButton);
          }
          if (isDisabled !== shouldDisableForm) {
            setIsDisabled(shouldDisableForm);
          }

          return (
            <form>
              <Grid style={pageGrid}>
                <Title title="Overview" />
                {shouldDisableForm && (
                  <AlreadySubmitted
                    pecrId={pecrId}
                    insertId={props.insertId}
                    pecrTitle={props.pecrTitle}
                  />
                )}
                <Grid
                  container
                  rowSpacing={1}
                  columnSpacing={{ xs: 1, sm: 2, md: 3 }}
                  style={overviewContainers}
                >
                  <OverviewInput
                    overviewInfo={overviewInfo}
                    handleOnTextChange={handleOnTextChange}
                    setFieldValue={setFieldValue}
                    values={values}
                    shouldDisableForm={shouldDisableForm}
                    inputValidationMessage={inputValidationMessage}
                    t={t}
                    ProductFamilyID={ProductFamilyID}
                    handleOnChange={handleOnChange}
                  />
                  <DriverSection
                    handleOnToggle={handleOnToggle}
                    setFieldValue={setFieldValue}
                    shouldDisableForm={shouldDisableForm}
                    values={values}
                  />
                </Grid>
                <hr />
                <Grid
                  container
                  columnSpacing={{ xs: 1, sm: 2, md: 3 }}
                  style={overviewContainers}
                >
                  <OverviewUploadFiles
                    shouldDisableForm={shouldDisableForm}
                    displayFiles={displayFiles}
                    setDisplayFiles={setDisplayFiles}
                    uploadedFiles={uploadedFiles}
                    setUploadedFiles={setUploadedFiles}
                    errorMessage={errorMessage}
                    setErrorMessage={setErrorMessage}
                    removeDoc={removeDoc}
                    onDownload={onDownloadClick}
                  />
                  <Grid item xs={6} style={listDiv} height={'40px'}>
                    <Button title={t('Cancel')} onClick={onCancelClick} />
                    <Button
                      title={t('Save')}
                      onClick={() => onSaveClick(values)}
                      disabled={
                        shouldDisableForm ||
                        shouldDisableButton ||
                        !LSCMailAddress
                      }
                    />
                    <Button
                      title={t('Submit')}
                      onClick={handleSubmit}
                      disabled={
                        shouldDisableForm ||
                        shouldDisableButton ||
                        !LSCMailAddress
                      }
                    />
                  </Grid>
                </Grid>
              </Grid>
            </form>
          );
        }}
      </Formik>
      {showProgress && (
        <Grid style={progressBar}>
          <LinearProgress color="success" sx={{ padding: '5px' }} />
        </Grid>
      )}
      <Toast />
    </>
  );
}

const mapStateToProps = (state = {}) => {
  return {
    overviewInfo: state.pecrInfo.overview,
    pecrTitle: state.pecrInfo.overview.Title,
    insertId: state.pecrInfo.pecrData.insertId,
    pecrStatus: state.pecrInfo.pecrData.pecrStatus,
    userInfo: state.userInfo,
    errorInAPI: state.errorNotification,
  };
};

export default connect(mapStateToProps, null)(CreateOverviewPage);
